import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import BottomNavigation from '../components/BottomNavigation';
import './OnlineChapterDetails.css';

const OnlineChapterDetails: React.FC = () => {
  const navigate = useNavigate();
  const { id, chapterId } = useParams();

  // Mock data
  const chapter = {
    id: 1,
    title: 'Introduction to Calculus',
    description: 'This chapter introduces the fundamental concepts of calculus through interactive live sessions.',
    sessions: [
      { id: 1, title: 'Session 1: What is Calculus?', date: '2024-02-10', time: '15:00', status: 'upcoming', hasRecording: false },
      { id: 2, title: 'Session 2: Understanding Limits', date: '2024-02-12', time: '15:00', status: 'past', hasRecording: true },
      { id: 3, title: 'Session 3: Introduction to Derivatives', date: '2024-02-14', time: '15:00', status: 'upcoming', hasRecording: false }
    ],
    materials: [
      { id: 1, type: 'PDF', title: 'Chapter Notes', size: '2.5 MB', icon: '📄' },
      { id: 2, type: 'Link', title: 'Additional Resources', icon: '🔗' },
      { id: 3, type: 'Video', title: 'Extra Examples', icon: '🎥' }
    ],
    quiz: {
      id: 1,
      title: 'Chapter Quiz',
      questionsCount: 10,
      duration: '15 min',
      isCompleted: false
    }
  };

  const handleSessionClick = (sessionId: number) => {
    navigate(`/online-course/${id}/session/${sessionId}`);
  };

  const handleQuizClick = () => {
    navigate(`/online-course/${id}/chapter/${chapterId}/quiz/${chapter.quiz.id}`);
  };

  const handleJoinSession = (e: React.MouseEvent, sessionId: number) => {
    e.stopPropagation();
    handleSessionClick(sessionId);
  };

  const handleWatchSession = (e: React.MouseEvent, sessionId: number) => {
    e.stopPropagation();
    handleSessionClick(sessionId);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  };

  return (
    <div className="online-chapter-details-page">
      <header className="online-chapter-details-header">
        <button className="back-button" onClick={() => navigate(-1)}>
          ←
        </button>
        <h1>{chapter.title}</h1>
        <div className="header-spacer"></div>
      </header>

      <div className="online-chapter-details-content">
        {/* Chapter Description */}
        <div className="chapter-description-card">
          <p>{chapter.description}</p>
        </div>

        {/* Sessions Section */}
        <div className="sessions-section">
          <h2 className="section-title">
            Sessions ({chapter.sessions.length})
          </h2>
          {chapter.sessions.map((session, index) => (
            <div
              key={session.id}
              className={`session-item-online ${session.status === 'upcoming' ? 'upcoming' : 'past'}`}
              onClick={() => handleSessionClick(session.id)}
            >
              <div className="session-number-online">{index + 1}</div>
              <div className="session-info-online">
                <h3 className="session-title-online">{session.title}</h3>
                <div className="session-meta-online">
                  <span>📅 {formatDate(session.date)}</span>
                  <span>🕐 {session.time}</span>
                  {session.status === 'upcoming' && <span className="status-badge-online">Upcoming</span>}
                  {session.status === 'past' && session.hasRecording && <span className="status-badge-online recording">Has Recording</span>}
                </div>
              </div>
              <div className="session-action-online">
                {session.status === 'upcoming' ? (
                  <button 
                    className="join-btn-online"
                    onClick={(e) => handleJoinSession(e, session.id)}
                  >
                    Join
                  </button>
                ) : session.hasRecording ? (
                  <button 
                    className="watch-btn-online"
                    onClick={(e) => handleWatchSession(e, session.id)}
                  >
                    Watch
                  </button>
                ) : (
                  <span className="no-recording-online">No Recording</span>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Materials Section */}
        {chapter.materials.length > 0 && (
          <div className="materials-section-online">
            <h2 className="section-title">Materials</h2>
            {chapter.materials.map((material) => (
              <div key={material.id} className="material-item-online">
                <div className="material-icon-online">{material.icon}</div>
                <div className="material-info-online">
                  <h3 className="material-title-online">{material.title}</h3>
                  {material.size && (
                    <span className="material-size-online">{material.size}</span>
                  )}
                </div>
                <button className="download-button-online">⬇</button>
              </div>
            ))}
          </div>
        )}

        {/* Quiz Section */}
        {chapter.quiz && (
          <div className="quiz-section-online">
            <div className="quiz-card-online">
              <div className="quiz-icon-online">📝</div>
              <div className="quiz-info-online">
                <h3>{chapter.quiz.title}</h3>
                <div className="quiz-meta-online">
                  <span>{chapter.quiz.questionsCount} questions</span>
                  <span>⏱️ {chapter.quiz.duration}</span>
                </div>
              </div>
              <button 
                className={`quiz-button-online ${chapter.quiz.isCompleted ? 'completed' : ''}`}
                onClick={handleQuizClick}
              >
                {chapter.quiz.isCompleted ? 'Review' : 'Start Quiz'}
              </button>
            </div>
          </div>
        )}
      </div>

      <BottomNavigation />
    </div>
  );
};

export default OnlineChapterDetails;
